/**
 * useDebouncedValue — returns `value` delayed by `delay` ms.
 *
 * The input remains synchronous (e.g. so a controlled input's caret stays
 * responsive); only the *consumer* sees the debounced shape, typically a
 * `filters` object that drives a TanStack Query refetch.
 *
 * An object is compared by CONTENT, not identity: a caller that builds a new object every render (most do) must not
 * restart the timer each time — two such values in one component would otherwise reset each other's timers
 * forever, and neither would ever settle.
 */

import { useEffect, useState } from 'react';

export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  const key = typeof value === 'object' ? JSON.stringify(value) : value;
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for `value` (same content = same value)
  }, [key, delay]);
  return debounced;
}
